import React, {useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';

import {text} from '../text';
import {hooks} from '../hooks';
import {custom} from '../custom';
import {svg} from '../assets/svg';
import {theme} from '../constants';
import {components} from '../components';
import axiosInstance from '../services/axiosInstance';
import {RootState} from '../store';
import { clearProfileCache, patchProfileCache } from '../utils/profileCache';
import { MdCheckCircle, MdError, MdWarning, MdCameraAlt, MdPhotoLibrary } from 'react-icons/md';
import { useLanguage } from '../context/LanguageContext';

const CheckCircleIcon = MdCheckCircle as any;
const ErrorIcon = MdError as any;
const WarningIcon = MdWarning as any;
const CameraIcon = MdCameraAlt as any;
const GalleryIcon = MdPhotoLibrary as any;

// ─── CropModal ──────────────────────────────────────────────────────────────
const CROP_CONTAINER = 280;
const CROP_CIRCLE_R = 125;

interface CropModalProps {
  src: string;
  onConfirm: (blob: Blob) => void;
  onCancel: () => void;
}

const CropModal: React.FC<CropModalProps> = ({ src, onConfirm, onCancel }) => {
  const { t } = useLanguage();
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const naturalSizeRef = useRef({ w: 0, h: 0 });

  const [imgState, setImgState] = useState({ x: 0, y: 0, w: CROP_CONTAINER });
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });

  const dragRef = useRef<{
    startX: number; startY: number;
    startImgX: number; startImgY: number;
  } | null>(null);
  const pinchRef = useRef<{
    startDist: number; startW: number;
    startX: number; startY: number;
  } | null>(null);

  const getImgH = (w: number, nat: { w: number; h: number }) =>
    nat.w && nat.h ? (nat.h / nat.w) * w : w;

  const getMinW = (nat: { w: number; h: number }) =>
    nat.w && nat.h && nat.w > nat.h
      ? (CROP_CIRCLE_R * 2) * nat.w / nat.h
      : CROP_CIRCLE_R * 2;

  const handleImgLoad = () => {
    if (!imgRef.current) return;
    const { naturalWidth: nw, naturalHeight: nh } = imgRef.current;
    const nat = { w: nw, h: nh };
    naturalSizeRef.current = nat;
    setNaturalSize(nat);
    // Cover the container initially
    const scale = Math.max(CROP_CONTAINER / nw, CROP_CONTAINER / nh);
    const initW = nw * scale;
    const initH = nh * scale;
    setImgState({
      x: (CROP_CONTAINER - initW) / 2,
      y: (CROP_CONTAINER - initH) / 2,
      w: initW,
    });
  };

  // Global mouse move/up
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setImgState(s => ({
        ...s,
        x: dragRef.current!.startImgX + dx,
        y: dragRef.current!.startImgY + dy,
      }));
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // Wheel zoom
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.1 : 0.9;
      const nat = naturalSizeRef.current;
      const minW = getMinW(nat);
      setImgState(s => {
        const oldH = getImgH(s.w, nat);
        const newW = Math.max(minW, Math.min(CROP_CONTAINER * 5, s.w * factor));
        const newH = getImgH(newW, nat);
        const cx = CROP_CONTAINER / 2;
        const cy = CROP_CONTAINER / 2;
        return {
          w: newW,
          x: cx - (cx - s.x) * (newW / s.w),
          y: cy - (cy - s.y) * (oldH > 0 ? newH / oldH : 1),
        };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const getTouchDist = (touches: TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = {
      startX: e.clientX, startY: e.clientY,
      startImgX: imgState.x, startImgY: imgState.y,
    };
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      const t0 = e.touches[0];
      dragRef.current = {
        startX: t0.clientX, startY: t0.clientY,
        startImgX: imgState.x, startImgY: imgState.y,
      };
      pinchRef.current = null;
    } else if (e.touches.length === 2) {
      dragRef.current = null;
      pinchRef.current = {
        startDist: getTouchDist(e.touches as any),
        startW: imgState.w,
        startX: imgState.x,
        startY: imgState.y,
      };
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    if (e.touches.length === 1 && dragRef.current) {
      const t0 = e.touches[0];
      const dx = t0.clientX - dragRef.current.startX;
      const dy = t0.clientY - dragRef.current.startY;
      setImgState(s => ({
        ...s,
        x: dragRef.current!.startImgX + dx,
        y: dragRef.current!.startImgY + dy,
      }));
    } else if (e.touches.length === 2 && pinchRef.current) {
      const dist = getTouchDist(e.touches as any);
      const ratio = dist / pinchRef.current.startDist;
      const nat = naturalSizeRef.current;
      const minW = getMinW(nat);
      const newW = Math.max(minW, Math.min(CROP_CONTAINER * 5, pinchRef.current.startW * ratio));
      const oldH = getImgH(pinchRef.current.startW, nat);
      const newH = getImgH(newW, nat);
      const cx = CROP_CONTAINER / 2;
      const cy = CROP_CONTAINER / 2;
      setImgState({
        w: newW,
        x: cx - (cx - pinchRef.current.startX) * (newW / pinchRef.current.startW),
        y: cy - (cy - pinchRef.current.startY) * (oldH > 0 ? newH / oldH : 1),
      });
    }
  };

  const handleTouchEnd = () => {
    dragRef.current = null;
    pinchRef.current = null;
  };

  const handleCrop = () => {
    if (!imgRef.current || !containerRef.current) return;
    const img = imgRef.current;
    const imgRect = img.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    const cropLeft = containerRect.left + (CROP_CONTAINER / 2 - CROP_CIRCLE_R);
    const cropTop = containerRect.top + (CROP_CONTAINER / 2 - CROP_CIRCLE_R);
    const diameter = CROP_CIRCLE_R * 2;

    const scaleX = img.naturalWidth / imgRect.width;
    const scaleY = img.naturalHeight / imgRect.height;

    const srcX = Math.max(0, (cropLeft - imgRect.left) * scaleX);
    const srcY = Math.max(0, (cropTop - imgRect.top) * scaleY);
    const srcW = Math.min(diameter * scaleX, img.naturalWidth - srcX);
    const srcH = Math.min(diameter * scaleY, img.naturalHeight - srcY);

    const outputSize = 400;
    const canvas = document.createElement('canvas');
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext('2d')!;
    ctx.beginPath();
    ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, outputSize, outputSize);

    canvas.toBlob(blob => { if (blob) onConfirm(blob); }, 'image/jpeg', 0.92);
  };

  const imgH = getImgH(imgState.w, naturalSize);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 5000,
      backgroundColor: 'rgba(0,0,0,0.92)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: 16, padding: 20,
    }}>
      <div style={{ color: 'white', fontSize: 17, fontWeight: 600 }}>
        {t.profile.cropTitle}
      </div>

      <div
        ref={containerRef}
        style={{
          width: CROP_CONTAINER, height: CROP_CONTAINER,
          position: 'relative', overflow: 'hidden',
          cursor: 'grab', userSelect: 'none', touchAction: 'none',
          borderRadius: 8, backgroundColor: '#111',
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <img
          ref={imgRef}
          src={src}
          alt="crop"
          onLoad={handleImgLoad}
          draggable={false}
          style={{
            position: 'absolute',
            left: imgState.x,
            top: imgState.y,
            width: imgState.w,
            height: imgH,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
        {/* Dimmed overlay with transparent circle hole */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: `radial-gradient(circle ${CROP_CIRCLE_R}px at 50% 50%, transparent ${CROP_CIRCLE_R - 0.5}px, rgba(0,0,0,0.65) ${CROP_CIRCLE_R}px)`,
        }} />
        {/* Circle border */}
        <div style={{
          position: 'absolute',
          left: CROP_CONTAINER / 2 - CROP_CIRCLE_R,
          top: CROP_CONTAINER / 2 - CROP_CIRCLE_R,
          width: CROP_CIRCLE_R * 2,
          height: CROP_CIRCLE_R * 2,
          borderRadius: '50%',
          border: '2px solid rgba(255,255,255,0.8)',
          pointerEvents: 'none',
          boxSizing: 'border-box',
        }} />
      </div>

      <div style={{ color: '#9CA3AF', fontSize: 13 }}>
        {t.profile.cropHint}
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        <button
          onClick={onCancel}
          style={{
            padding: '12px 28px', borderRadius: 24,
            border: '1px solid #6B7280', background: 'transparent',
            color: 'white', fontSize: 15, cursor: 'pointer',
          }}
        >
          {t.common.cancel}
        </button>
        <button
          onClick={handleCrop}
          style={{
            padding: '12px 28px', borderRadius: 24, border: 'none',
            background: theme.colors.mainColor, color: 'white',
            fontSize: 15, fontWeight: 600, cursor: 'pointer',
          }}
        >
          {t.profile.cropConfirm}
        </button>
      </div>
    </div>
  );
};

// ─── EditPersonalInfo ────────────────────────────────────────────────────────
export const EditPersonalInfo: React.FC = () => {
  const {pathname} = useLocation();
  const navigate = hooks.useAppNavigate();
  const authSlice = hooks.useAppSelector((state: RootState) => state.authSlice);
  const { t } = useLanguage();

  const [name, setName] = useState(authSlice.userName || '');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState(authSlice.email || '');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [currentPhoto, setCurrentPhoto] = useState<string | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showPhotoSheet, setShowPhotoSheet] = useState(false);

  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL || '';

  const [alertConfig, setAlertConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning';
    onClose?: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'success' });

  useEffect(() => {
    if (authSlice.userId) {
      const fetchUser = async () => {
        try {
          setIsFetching(true);
          const response = await axiosInstance.get(`/users/${authSlice.userId}`);
          const userData = response.data?.data || response.data || {};
          setName(userData.name || '');
          setPhone(userData.phone || '');
          setEmail(userData.email || '');
        } catch (error) {
          console.error('Failed to fetch user details', error);
        } finally {
          setIsFetching(false);
        }
      };
      fetchUser();
    }
  }, [authSlice.userId]);

  // Fetch current photo from employee profile
  useEffect(() => {
    axiosInstance.get('/employees/profile')
      .then(res => setCurrentPhoto(res.data?.photo || null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setTimeout(() => {
      window.scroll({top: -1, left: 0, behavior: 'smooth'});
    }, 10);
  }, [pathname]);

  const showAlert = (title: string, message: string, type: 'success' | 'error' | 'warning' = 'success', onClose?: () => void) => {
    setAlertConfig({ isOpen: true, title, message, type, onClose });
  };

  const hideAlert = () => {
    const onClose = alertConfig.onClose;
    setAlertConfig(prev => ({ ...prev, isOpen: false }));
    if (onClose) onClose();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const url = URL.createObjectURL(file);
    setCropSrc(url);
    setShowPhotoSheet(false);
  };

  const handleCropConfirm = async (blob: Blob) => {
    const prevSrc = cropSrc;
    setCropSrc(null);
    setIsUploading(true);
    try {
      const fd = new FormData();
      fd.append('photo', blob, 'profile.jpg');
      const res = await axiosInstance.post('/employees/upload-photo', fd);
      setCurrentPhoto(res.data.photo);
      patchProfileCache(authSlice.userId!, { photo: res.data.photo });
    } catch {
      showAlert(t.common.error, t.profile.photoUpdateFailed, 'error');
    } finally {
      setIsUploading(false);
      if (prevSrc) URL.revokeObjectURL(prevSrc);
    }
  };

  const handleCropCancel = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };

  const handleSave = async () => {
    if (!authSlice.userId) return;
    try {
      setIsLoading(true);
      await axiosInstance.post(`/users/${authSlice.userId}/updateUser`, {
        name,
        phone,
        email,
        password: password || undefined,
      });
      clearProfileCache(authSlice.userId!);
      showAlert(t.common.success, t.profile.profileUpdated, 'success', () => {
        navigate(-1);
      });
    } catch (error) {
      console.error('Failed to update profile:', error);
      showAlert(t.common.error, t.profile.failedUpdate, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const renderHeader = (): JSX.Element => (
    <components.Header title={t.profile.editPersonalInfo} goBack={true} />
  );

  const renderPhotoSection = (): JSX.Element => {
    const photoUrl = currentPhoto ? `${imageBaseUrl}${currentPhoto}` : null;
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24, marginTop: 8 }}>
        <div
          style={{ position: 'relative', cursor: 'pointer' }}
          onClick={() => setShowPhotoSheet(true)}
        >
          <div style={{
            width: 90, height: 90, borderRadius: '50%',
            overflow: 'hidden', border: `3px solid ${theme.colors.mainColor}`,
            backgroundColor: '#F3F4F6',
          }}>
            {photoUrl ? (
              <img src={photoUrl} alt="profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <img src="/images/logo.png" alt="profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
          </div>
          {/* Upload spinner */}
          {isUploading && (
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <style>{`@keyframes _spin{to{transform:rotate(360deg)}}`}</style>
              <div style={{
                width: 28, height: 28,
                border: '3px solid rgba(255,255,255,0.4)',
                borderTop: '3px solid white',
                borderRadius: '50%',
                animation: '_spin 0.8s linear infinite',
              }} />
            </div>
          )}
          {/* Camera badge */}
          {!isUploading && (
            <div style={{
              position: 'absolute', bottom: 0, right: 0,
              width: 28, height: 28, borderRadius: '50%',
              backgroundColor: theme.colors.mainColor,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '2px solid white',
            }}>
              <CameraIcon size={14} color="white" />
            </div>
          )}
        </div>

        {/* Hidden file inputs */}
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </div>
    );
  };

  const renderContent = (): JSX.Element => {
    if (isFetching) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <text.T16>{t.common.loading}</text.T16>
        </div>
      );
    }

    return (
      <main className='container' style={{marginTop: 52 + 10, marginBottom: 20}}>
        {renderPhotoSection()}
        <custom.InputField
          leftIcon={<svg.UserSvg />}
          placeholder={t.profile.name}
          value={name}
          onChange={(e) => setName(e.target.value)}
          containerStyle={{marginBottom: 10}}
        />
        <custom.InputField
          leftIcon={<svg.PhoneSvg />}
          placeholder={t.profile.phone}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          containerStyle={{marginBottom: 10}}
        />
        <custom.InputField
          leftIcon={<svg.EmailSvg />}
          placeholder={t.profile.email}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          containerStyle={{marginBottom: 10}}
        />
        <custom.InputField
          leftIcon={<svg.PasswordSvg />}
          rightIcon={<svg.EyeOffSvg />}
          placeholder={t.profile.passwordOptional}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          isPasswordField={true}
          clickable={true}
          containerStyle={{marginBottom: 14}}
        />
        <components.Button
          title={isLoading ? t.common.saving : t.common.save}
          onClick={handleSave}
        />
      </main>
    );
  };

  const renderPhotoSheet = (): JSX.Element | null => {
    if (!showPhotoSheet) return null;
    return (
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 4000,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        }}
        onClick={() => setShowPhotoSheet(false)}
      >
        <div
          style={{
            backgroundColor: 'white', borderRadius: '20px 20px 0 0',
            padding: '20px 20px 48px',
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{
            width: 40, height: 4, borderRadius: 2,
            backgroundColor: '#D1D5DB', margin: '0 auto 20px',
          }} />
          <text.H5 style={{ marginBottom: 20, textAlign: 'center' }}>
            {t.profile.changePhoto}
          </text.H5>
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 16,
              padding: '14px 4px', cursor: 'pointer',
              borderBottom: '1px solid #F3F4F6',
            }}
            onClick={() => galleryInputRef.current?.click()}
          >
            <GalleryIcon size={22} color={theme.colors.mainColor} />
            <text.T16>{t.profile.fromGallery}</text.T16>
          </div>
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 16,
              padding: '14px 4px', cursor: 'pointer',
            }}
            onClick={() => cameraInputRef.current?.click()}
          >
            <CameraIcon size={22} color={theme.colors.mainColor} />
            <text.T16>{t.profile.takePhoto}</text.T16>
          </div>
        </div>
      </div>
    );
  };

  const renderAlert = (): JSX.Element | null => {
    if (!alertConfig.isOpen) return null;
    return (
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 4000,
          display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20,
        }}
      >
        <div
          style={{
            backgroundColor: 'white', borderRadius: 16, padding: 24,
            width: '100%', maxWidth: 320, textAlign: 'center',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
          }}
        >
          <div style={{marginBottom: 16}}>
            {alertConfig.type === 'success' && <CheckCircleIcon size={60} color="#10B981" />}
            {alertConfig.type === 'error' && <ErrorIcon size={60} color="#EF4444" />}
            {alertConfig.type === 'warning' && <WarningIcon size={60} color="#F59E0B" />}
          </div>
          <text.H4 style={{ marginBottom: 8 }}>{alertConfig.title}</text.H4>
          <text.T16 style={{ marginBottom: 24, color: theme.colors.bodyTextColor, lineHeight: 1.5 }}>
            {alertConfig.message}
          </text.T16>
          <components.Button title={t.common.ok} onClick={hideAlert} containerStyle={{width: '100%'}} />
        </div>
      </div>
    );
  };

  return (
    <div id='screen'>
      <components.Background />
      {renderHeader()}
      {renderContent()}
      {renderPhotoSheet()}
      {renderAlert()}
      {cropSrc && (
        <CropModal
          src={cropSrc}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
};
